import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CITIES, CITY_SLUGS, type City } from "@/data/cities";
import { useReveal } from "@/hooks/use-reveal";
import { RentalPromo } from "@/components/rental-promo";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";
import { INDEXABLE_SERVICE_CITIES } from "@/data/city-service-content";
import { ANNIVERSAIRE_CONTENT } from "@/data/city-anniversaire";
import { CityServiceBlocks } from "@/components/city-service-blocks";
import { RelatedArticles } from "@/components/related-articles";
import { lazy, Suspense } from "react";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";

const GoogleReviewsCarousel = lazy(() =>
  import("@/components/google-reviews-carousel").then((m) => ({ default: m.GoogleReviewsCarousel })),
);


export const Route = createFileRoute("/dj-anniversaire/$ville")({
  loader: ({ params }) => {
    const city = CITIES[params.ville];
    if (!city) throw notFound();
    const indexable = (INDEXABLE_SERVICE_CITIES as readonly string[]).includes(params.ville);
    return { city, indexable };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.city as City | undefined;
    const indexable = loaderData?.indexable ?? false;
    if (!c) return { meta: [{ title: "DJ Anniversaire" }] };

    const prep = c.preposition || "à";
    const content = ANNIVERSAIRE_CONTENT[c.slug];
    const title = `DJ anniversaire ${c.name} | DJ Val`;
    const desc = content
      ? content.intro
      : `DJ pour votre anniversaire ${prep} ${c.name} : 30, 40, 50 ou 60 ans, surprise party. Sono, lumière et animation toutes générations. Devis gratuit en 24 h.`;
    const faq = content?.faq ?? [
      { q: `Quel tarif pour un anniversaire ${prep} ${c.name} ?`, a: "À partir de 600 € pour 4h, sono + lumière incluses. Forfait nuit complète et options photobooth/machine à fumée disponibles." },
      { q: `Pouvez-vous animer en villa ou à domicile ${prep} ${c.name} ?`, a: "Oui, setup compact pour intérieur ou terrasse, sécurisation des câbles, gestion du volume pour respecter le voisinage." },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: metaDesc(desc) },
        ...(indexable ? [] : [{ name: "robots", content: "noindex,follow" }]),
        { property: "og:title", content: title },
        { property: "og:description", content: metaDesc(desc) },
        { property: "og:image", content: c.hero },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://dj-val.fr/dj-anniversaire/${c.slug}` },
      ],
      links: [
        { rel: "canonical", href: `https://dj-val.fr/dj-anniversaire/${c.slug}` },
        { rel: "preload", as: "image", href: c.hero, fetchpriority: "high" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "DJ Anniversaire",
            provider: { ...LOCAL_BUSINESS_SCHEMA },
            areaServed: [{ "@type": "Place", name: c.name }, "Cavaillon", "Avignon", "Vaucluse"],
            description: desc,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
              { "@type": "ListItem", position: 2, name: "Soirées & Événements", item: "https://dj-val.fr/soirees-evenements" },
              { "@type": "ListItem", position: 3, name: c.name, item: `https://dj-val.fr/dj-anniversaire/${c.slug}` },
            ],
          }),
        },
      ],

    };
  },
  notFoundComponent: () => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="font-serif-display text-4xl">Ville non couverte</h1>
        <Link to="/soirees-evenements" className="mt-4 inline-block text-[var(--gold)] underline">Retour aux soirées</Link>
      </div>
    </div>
  ),
  component: AnnivPage,
});

function AnnivPage() {
  useReveal();
  const { city } = Route.useLoaderData();
  const c: City = city;
  const prep = c.preposition || "à";
  const content = ANNIVERSAIRE_CONTENT[c.slug];


  return (
    <div className="bg-background text-muted-foreground">
      <section className="on-dark relative min-h-[80vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img src={c.hero} alt={`DJ anniversaire ${prep} ${c.name}`} className="h-full w-full object-cover opacity-40 kenburns-img" fetchPriority="high" decoding="async" loading="eager" width={1920} height={1080} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, var(--ink) 0%, color-mix(in oklab, var(--ink) 70%, transparent) 55%, color-mix(in oklab, var(--ink) 30%, transparent) 100%), linear-gradient(to bottom, var(--ink) 0%, transparent 40%, var(--ink) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">Anniversaire · {c.region}</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">DJ Anniversaire</span>
            <span className="block font-light gold-text-shine mt-2">{prep} {c.name}</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">
            {content?.intro ??
              `30, 40, 50, 60 ans, surprise party ou anniversaire ado ${prep} ${c.name} : un DJ pro qui lit votre dancefloor, fédère toutes les générations et transforme votre soirée en moment inoubliable.`}
          </p>

          <div className="flex flex-wrap gap-4">
            <Link to="/contactez-nous" className="bg-[var(--gold)] text-[var(--ink)] px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">Devis anniversaire</Link>
            <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_anniversaire_ville" })} className="border border-[var(--gold-line)] text-foreground px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">07 67 90 86 67</a>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Pourquoi DJ Val</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">Le DJ qui <span className="italic">fait danser</span> {c.name}</h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {[
            { title: "Toutes générations", text: "Variété française, disco, années 80-90, hits 2000 et actuels — chaque âge retrouve sa piste de danse." },
            { title: "Forfait clé en main", text: "Sono, éclairage LED, micros, installation et démontage compris " + prep + " " + c.name + ". Vous ne vous occupez de rien." },
            { title: "Effets de fête", text: "Machine à fumée, lasers, photobooth en option pour transformer votre soirée en vraie ambiance club." },
          ].map((h, i) => (
            <div key={h.title} className="bg-background p-10 sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{h.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{h.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-4xl mx-auto sr" data-reveal>
          <p className="eyebrow text-[var(--gold)] text-center">Tous formats</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4 text-center">Quel que soit votre âge {prep} {c.name}</h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {["30 ans — playlist 2000s / 2010s", "40 ans — disco, funk, années 90", "50 ans — variété FR, slows, dancefloor familial", "60 ans — golden hits, rock'n'roll, valse", "Surprise party — coordination secrète", "Anniversaire ado — hits actuels, karaoké"].map((f) => (
              <div key={f} className="border border-[var(--gold-line)] bg-background p-5 text-foreground/85 text-sm font-light">{f}</div>
            ))}
          </div>
        </div>
      </section>

      {content && <CityServiceBlocks content={content} cityName={c.name} />}

      <RentalPromo cityName={c.name} prep={prep} context="anniversaire" />

      <RelatedArticles
        slugs={[
          "playlist-mariage-50-morceaux-qui-font-danser",
          "prix-dj-mariage-provence-guide-complet-2026",
          "dj-vs-groupe-live-mariage-comparatif",
          "retroplanning-musique-mariage-12-mois",
        ]}
        title={`À lire aussi — préparer votre soirée ${prep} ${c.name}`}
      />
      <Suspense fallback={<div className="min-h-[30vh]" />}>
        <GoogleReviewsCarousel />
      </Suspense>

      <section className="py-24 px-6 text-center border-t border-[var(--gold-line)]">


        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">Votre anniversaire {prep} {c.name} <span className="italic text-[var(--gold)]">mérite</span> une vraie soirée</h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">Demander un devis</Link>
        </div>
      </section>

      <section className="py-16 px-6 border-t border-[var(--gold-line)]">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Autres prestations {prep} {c.name}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <Link to="/dj-mariage/$ville" params={{ ville: c.slug }} className="border border-[var(--gold-line)] bg-card p-6 text-left hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">DJ mariage {prep} {c.name}</p>
              <span className="gold-rule !w-8 my-3" />
              <p className="text-xs text-muted-foreground font-light">Animation mariage complète — cérémonie, cocktail, dîner, soirée.</p>
            </Link>
            <Link to="/dj-entreprise/$ville" params={{ ville: c.slug }} className="border border-[var(--gold-line)] bg-card p-6 text-left hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">DJ soirée d'entreprise {prep} {c.name}</p>
              <span className="gold-rule !w-8 my-3" />
              <p className="text-xs text-muted-foreground font-light">Gala, séminaire, kick-off, soirée CE — facturation pro, SACEM, RC Pro.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow text-[var(--gold)] text-center">Autres villes</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {CITY_SLUGS.filter((s) => s !== c.slug).map((s) => (
              <Link key={s} to="/dj-anniversaire/$ville" params={{ ville: s }} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
                {CITIES[s].name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
